import { Stack, type StackProps } from "@mui/material";
import { useHeroBackground } from "Components/Layout/HeroBackground/HeroBackgroundContext";
import { useHasBackgroundImage } from "Hooks/Layout/useHasBackgroundImage";

type Props = Pick<StackProps, "gap" | "children">;

/** Extra dark image below the hero content, before the first light widget starts (32px). */
const HERO_BOTTOM_PADDING = 8;
/** How far the first light widget rides up onto the dark image (24px). */
const NEXT_WIDGET_PULL_UP = 6;

/**
 * The part of a dashboard that sits on the dark hero background. AuthenticatedLayout stretches
 * the background to this element's bottom edge, so whatever the page puts here — the balance,
 * its actions, the stories row when there are stories — stays on dark, and everything after it
 * gets the light page background. Rendered as a Stack so the page's spacing continues inside.
 *
 * With the background present, the section pads itself at the bottom so the image shows below
 * the content, and pulls the following widget up so its card overlaps the image's edge. That
 * negative margin is an outer margin on purpose: the overlap is this section's own visual
 * treatment, and only it knows whether the background is there to overlap.
 */
export const HeroSection = ({ gap, children }: Props) => {
  const heroBackground = useHeroBackground();
  const { hasBackgroundImage } = useHasBackgroundImage();

  return (
    <Stack
      gap={gap}
      pb={hasBackgroundImage ? HERO_BOTTOM_PADDING : undefined}
      mb={hasBackgroundImage ? -NEXT_WIDGET_PULL_UP : undefined}
      ref={heroBackground?.setHeroElement}
    >
      {children}
    </Stack>
  );
};
